/**
 * In-place refresh of the "Open chat" badges (JAA-355) after an in-place
 * login. Email/password login flips isLoggedIn without a page reload, so rows
 * fetched anonymously are missing their conversation_id annotation. Rather
 * than refetching whole lists (which would reset pagination, loading states
 * and scroll position), the surfaces ask the backend for just the
 * {listing_id: conversation_id} map of the rows they already hold and merge
 * it by id — row identity and order never change, so only the badged buttons
 * re-render.
 */
import { useEffect, useRef } from 'react';
import { apiRequest } from './api';

/** Fetch the viewer's conversation ids for the given listing ids. */
export async function fetchConversationIdMap(
  listingIds: string[],
): Promise<Record<string, string>> {
  if (!listingIds.length) return {};
  const data = await apiRequest('/listing-conversation-ids', {
    method: 'POST',
    body: { listing_ids: listingIds },
  });
  return (data && data.conversation_ids) || {};
}

/**
 * Merge the fetched map into loaded rows by _id. Returns the SAME array when
 * nothing matched, so callers inside setState skip the re-render entirely.
 */
export function mergeConversationIds<
  T extends { _id?: string; conversation_id?: string | null },
>(rows: T[], map: Record<string, string>): T[] {
  let changed = false;
  const next = rows.map((row) => {
    const conv = row._id ? map[row._id] : undefined;
    if (!conv || row.conversation_id === conv) return row;
    changed = true;
    return { ...row, conversation_id: conv } as T;
  });
  return changed ? next : rows;
}

/**
 * Run `refresh` exactly when isLoggedIn transitions false -> true (an
 * in-place login). Logout always reloads the page, so no downgrade path.
 */
export function useConversationBadgeRefreshOnLogin(
  isLoggedIn: boolean | undefined,
  refresh: () => void,
): void {
  const prevRef = useRef(isLoggedIn);
  useEffect(() => {
    const was = prevRef.current;
    prevRef.current = isLoggedIn;
    if (was === false && isLoggedIn === true) refresh();
  }, [isLoggedIn, refresh]);
}
